import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiX, FiAward } from 'react-icons/fi';
import confetti from 'canvas-confetti';

import JajoIcon from '../../img/icons/jajo.png';
import JajoSzaryIcon from '../../img/icons/jajo_szary.png';
import PisklakIcon from '../../img/icons/pisklak.png';
import OrzelekIcon from '../../img/icons/orzelek.png';
import MieczIcon from '../../img/icons/miecz.png';

const gamificationLevels = [
    { name: "Jajo",          icon: { achieved: JajoIcon,    notAchieved: JajoSzaryIcon } },
    { name: "Pisklak",       icon: { achieved: PisklakIcon, notAchieved: JajoSzaryIcon } },
    { name: "Orzełek",       icon: { achieved: OrzelekIcon, notAchieved: JajoSzaryIcon } },
    { name: "Łowca zadań",   icon: { achieved: MieczIcon,   notAchieved: JajoSzaryIcon } },
];

const levelDescriptions = {
    "Jajo": "Jesteś na początku swojej drogi do sukcesu, ale już dokonałeś znaczących postępów - tak trzymaj!",
    "Pisklak": "Potrafisz poradzić sobie z większością łatwych zadań i pewniaczków - to świetna baza do dalszego rozwoju!",
    "Orzełek": "Dobrze radzisz sobie nawet z trudniejszymi i bardziej skomplikowanymi zadaniami - oby tak dalej, a zdasz maturę bez problemu!",
    "Łowca zadań": "Nawet najtrudniejsze zadania nie stanowią dla Ciebie problemu, a na maturze dasz sobie radę nawet z zamkniętymi oczami!"
};

const levelIconMap = gamificationLevels.reduce((acc, l) => { acc[l.name] = l.icon; return acc; }, {});

const AchievementPopup = ({ achievements, onClose }) => {
    useEffect(() => {
        confetti({
            particleCount: 120,
            spread: 80,
            origin: { y: 0.55 },
            colors: ['#F8AB3B', '#E09F2E', '#233855', '#ffffff'],
            zIndex: 10000,
        });
    }, []);

    return ReactDOM.createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9998] flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.92, opacity: 0, y: 12 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ type: "spring", damping: 24, stiffness: 280 }}
                    className="relative z-10 bg-white w-full max-w-sm rounded-2xl p-6 shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] border border-gray-100 text-center"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 p-2 text-gray-300 hover:text-gray-500 transition-colors rounded-full hover:bg-gray-50"
                    >
                        <FiX size={20} />
                    </button>

                    {/* Ikona + tytuł */}
                    <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1, type: "spring", stiffness: 220 }}
                        className="flex justify-center mb-3"
                    >
                        <div className="p-3 rounded-2xl bg-[#233855] shadow-md">
                            <FiAward className="text-[#F8AB3B]" size={28} />
                        </div>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="text-2xl font-extrabold font-apotek tracking-widest uppercase text-[#233855] mb-1"
                    >
                        Nowy poziom!
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-sm text-gray-400 mb-5"
                    >
                        Osiągnięto nowy poziom zaawansowania
                    </motion.p>

                    {/* Karty osiągnięć */}
                    <div className="space-y-4 mb-5">
                        {achievements.map((ach, index) => {
                            const oldIcon = levelIconMap[ach.oldLevel?.name]?.achieved || JajoSzaryIcon;
                            const newIcon = levelIconMap[ach.newLevel.name]?.achieved;

                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.25 + index * 0.1 }}
                                >
                                    <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl">
                                        <p className="text-xs font-bold text-[#233855] font-apotek tracking-widest uppercase mb-3">
                                            {ach.fieldName}
                                        </p>
                                        <div className="flex items-center justify-center gap-3">
                                            <div className="flex flex-col items-center w-24 opacity-50">
                                                <img src={oldIcon} alt={ach.oldLevel?.name || "—"} className="w-16 h-16 object-contain" />
                                                <span className="text-[10px] text-gray-400 mt-1 font-medium">{ach.oldLevel?.name || "—"}</span>
                                            </div>

                                            <div className="flex flex-col items-center gap-1">
                                                <FiArrowRight className="text-[#E09F2E]" size={20} />
                                            </div>

                                            <div className="flex flex-col items-center w-24">
                                                <motion.img
                                                    initial={{ scale: 0.7, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    transition={{ delay: 0.4 + index * 0.1, type: "spring", stiffness: 200 }}
                                                    src={newIcon}
                                                    alt={ach.newLevel.name}
                                                    className="w-16 h-16 object-contain drop-shadow-md"
                                                />
                                                <span className="text-xs font-bold text-[#233855] mt-1">{ach.newLevel.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2 px-2 leading-relaxed">
                                        {levelDescriptions[ach.newLevel.name]}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>

                    <motion.button
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        onClick={onClose}
                        className="w-full py-3.5 rounded-xl bg-[#233855] text-white font-bold font-apotek tracking-widest uppercase text-sm hover:bg-[#1a2b42] active:scale-[0.98] transition-all shadow-lg shadow-[#233855]/20"
                    >
                        Super!
                    </motion.button>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
};

export default AchievementPopup;
